import * as _ from "lodash";
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import * as languageProviderUtilities from "./languageProviderUtilities";
import { ICompletionTypes } from "../model/model";
import { Utilities } from "../../utilities";
import colors from "shared/styles/colors";
import { ThemeType } from "shared/styles/theme";
class LanguageProvider {
    private lang = "";
    private langSetterCallback: ((language: string) => void) | undefined;
    public setNewLang(lang: string): void {
        this.lang = lang;
        if (this.langSetterCallback) {
            this.langSetterCallback(lang)
        }
    }

    public registerLanguageProvider(monacoInstance: typeof monaco, completionTypes: ICompletionTypes, setLangCallback?: (language: string) => void, initialLanguage?: string) {
        const themeType = document.getElementById("theme")?.innerText;
        const isLightMode = ThemeType[themeType as keyof typeof ThemeType] === ThemeType.light;

        const lang = initialLanguage ?? this.lang;

        languageProviderUtilities.registerMonarchTokens(monacoInstance);
        languageProviderUtilities.setJSONDiagnosticOptions(monacoInstance);        

        monacoInstance.editor.defineTheme(languageProviderUtilities.theme, {
            base: isLightMode ? "vs" : "vs-dark",
            inherit: true,
            rules: languageProviderUtilities.getTokenRules(lang, isLightMode ? ThemeType.light : ThemeType.dark),
            colors: isLightMode ? {} : {
                "editor.background": colors.dark2
            }
        });

        function getTextBetweenLeadingHashAndCurrentCaretPosition(model: monaco.editor.ITextModel, position: monaco.Position) {
            const line = model.getLineContent(position.lineNumber);
            const leadingHashPosition = line.lastIndexOf("#", position.column);
            return {
                word: model.getLineContent(position.lineNumber).substring(leadingHashPosition, position.column),
                startColumn: leadingHashPosition + 1,
                endColumn: position.column
            }
        }

        this.langSetterCallback = setLangCallback;

        const dispose = monacoInstance.languages.registerCompletionItemProvider(lang, {
            triggerCharacters: ["#"],
            provideCompletionItems(model, position) {
                const snippets = languageProviderUtilities.filterSnippets(lang, completionTypes.snippets);
                const wholeWord = getTextBetweenLeadingHashAndCurrentCaretPosition(model, position);
                const range = {
                    startLineNumber: position.lineNumber,
                    endLineNumber: position.lineNumber,
                    startColumn: wholeWord.startColumn,
                    endColumn: wholeWord.endColumn
                };
                return {
                    suggestions: languageProviderUtilities.buildDependencyProposals(range, completionTypes, snippets)
                };
            },
            resolveCompletionItem: async function (item: monaco.languages.CompletionItem) {
                const label = item.label.toString();
                if (label.startsWith("#env.")) {
                    let environmentVariableName = label.substring(5);
                    if (environmentVariableName.includes(".")) { // group
                        environmentVariableName = environmentVariableName.substring(0, environmentVariableName.indexOf("."));
                    }
                    try {
                        const description = await Utilities.getFetch<string>(`api/environmentVariable/getSchemaDescriptionByName/${environmentVariableName}`);
                        item.documentation = description;
                    } catch (e) {
                        console.error("Failed to fetch description: " + e)
                    }
                    return item;
                } else if (label.startsWith("#snippet.")) {
                    const truncatedSnippet = _.truncate(item.insertText, { length: 100 });
                    item.documentation = truncatedSnippet;
                }
            }
        })
        return dispose;
    }
}

export const languageProvider = new LanguageProvider();
